import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyNotifications, markNotificationRead, markAllNotificationsRead } from '../services/api';
import { NotificationsSkeleton } from '../components/SkeletonLoader';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Maps a notification title to its destination admin route
  const getNotificationRoute = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('access request') || t.includes('handover')) return '/admin/access';
    if (t.includes('accreditation document') || t.includes('bulk accreditation') || t.includes('waiver link')) return '/admin/accreditation';
    if (t.includes('off-campus document') || t.includes('bulk off-campus')) return '/admin/off-campus';
    if (t.includes('deadline reminder')) return '/admin/settings/deadline';
    return null; // no specific destination
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await getMyNotifications();
      setNotifications(res.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleNotificationClick = async (n) => {
    if (!n.is_read) {
      try {
        await markNotificationRead(n.notification_id);
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.notification_id === n.notification_id ? { ...notif, is_read: true } : notif
          )
        );
      } catch (error) {
        console.error('Failed to mark as read:', error);
      }
    }
    const route = getNotificationRoute(n.title);
    if (route) navigate(route);
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const formatTime = (createdAt) => {
    if (!createdAt) return '';
    const diff = Date.now() - new Date(createdAt).getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  if (loading) {
    return (
      <div className="max-w-4xl space-y-6">
        <div className="skeleton h-8 w-44 mb-6" />
        <NotificationsSkeleton rows={6} />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-[24px] font-bold text-gray-900 tracking-tight">Notifications</h3>
          {unreadCount > 0 && (
            <p className="text-sm text-gray-500 mt-0.5">
              {unreadCount} unread
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="text-sm font-semibold text-[#086C53] hover:underline"
          >
            Mark all as read
          </button>
        )}
      </div>
      <div className="bg-white rounded-[20px] shadow-sm border border-gray-200 overflow-hidden divide-y divide-gray-100">
        {notifications.length > 0 ? notifications.map((n) => (
          <div
            key={n.notification_id}
            onClick={() => handleNotificationClick(n)}
            className={`p-6 flex justify-between items-start transition-colors cursor-pointer ${
              !n.is_read
                ? 'bg-[#f0f9f6]/40 hover:bg-[#e6f5f0]'
                : 'hover:bg-gray-50'
            }`}
          >
            <div className="flex space-x-4">
              <div className="mt-1">
                {!n.is_read ? (
                  <span className="flex w-3 h-3 bg-[#086C53] rounded-full"></span>
                ) : (
                  <span className="flex w-3 h-3 border-2 border-gray-300 rounded-full"></span>
                )}
              </div>
              <div>
                <h4 className={`text-[15px] ${!n.is_read
                  ? 'font-bold text-gray-900'
                  : 'font-semibold text-gray-700'}`}>
                  {n.title}
                </h4>
                <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">{n.message}</p>
              </div>
            </div>
            <span className="text-xs font-semibold text-gray-400 whitespace-nowrap ml-4">
              {formatTime(n.created_at)}
            </span>
          </div>
        )) : (
          <div className="p-8 text-center text-gray-500">
            No notifications yet.
          </div>
        )}
      </div>
    </div>
  );
};

export default Notifications;
